// Antwort-Editor mit Bob-Leiste, Absenderwahl, Senden & erledigen, 10 s Rückgängig.
import { useCallback, useEffect, useRef, useState } from 'react';
import { PRUEFEN_RE, type BobKarte, type BobResult, type Draft, type Mailbox } from '@mail/shared';
import { api, ApiError } from '../lib/api.ts';
import { useStore } from '../lib/store.ts';
import { countPruefen, Formatleiste, MailEditor, useMailEditor } from './Editor.tsx';
import { BausteinePopover } from './Bausteine.tsx';
import { AdressFeld } from './AdressFeld.tsx';
import { bytes } from '../lib/format.ts';
import { joinAddresses, parseAddresses, type AddressInput } from '../lib/addresses.ts';
import { sendeWarnungen } from '../lib/warnungen.ts';
import { splitZitat, zitatVorschau } from '../lib/zitat.ts';
import { toast } from './Toast.tsx';

type Props = {
  draft: Draft;
  mailboxes: Mailbox[];
  bobEnabled: boolean; // Postfach mit CRM-Kontext
  standalone?: boolean; // eigenes Fenster /compose
  chips?: string[];
  leadChip?: boolean; // Absender nicht im CRM → „Lead anlegen (Karte)“
  onSent: (opts: {
    thenDone: boolean;
    draftId: string;
    sendAt?: string;
    undo: () => Promise<void>;
  }) => void;
  onDiscard: () => void;
};

export function ReplyBox({
  draft,
  mailboxes,
  bobEnabled,
  standalone,
  chips = [],
  leadChip,
  onSent,
  onDiscard,
}: Props) {
  const me = useStore((s) => s.me);
  const [d, setD] = useState(draft);
  const [to, setTo] = useState(joinAddresses(draft.to));
  const [cc, setCc] = useState(joinAddresses(draft.cc));
  const [showCc, setShowCc] = useState(draft.cc.length > 0);
  const [subject, setSubject] = useState(draft.subject);
  const [mailboxId, setMailboxId] = useState(draft.mailboxId);
  // Zitierter Verlauf bleibt außerhalb des Editors (eingeklappt, Original-HTML) und wird beim
  // Speichern/Senden wieder angehängt; `html` ist immer eigener Teil + Zitat.
  const [start] = useState(() => splitZitat(draft.html));
  const [zitat, setZitat] = useState(start.zitat);
  const zitatRef = useRef(start.zitat);
  const [html, setHtml] = useState(start.eigen + start.zitat);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState<'' | 'speichert' | 'gespeichert'>('');
  const [bobText, setBobText] = useState('');
  const [bob, setBob] = useState<{ running: boolean; result: BobResult | null }>({
    running: false,
    result: null,
  });
  const [karten, setKarten] = useState<
    Array<BobKarte & { status?: 'ok' | 'fehler' | 'verworfen'; meldung?: string }>
  >([]);
  const [sending, setSending] = useState(false);
  const [bestaetigt, setBestaetigt] = useState(false);
  const [spaeter, setSpaeter] = useState(false);
  const [bausteine, setBausteine] = useState(false);
  // Kollisionsschutz: solange der Editor offen ist, alle 30 s „ich schreibe“ melden
  useEffect(() => {
    const tid = draft.threadId;
    if (!tid) return;
    const melden = () =>
      void api
        .presence(tid, true)
        .then((r) => {
          if (r.andere.length) toast(`${r.andere.join(', ')} schreibt hier auch gerade`);
        })
        .catch(() => undefined);
    melden();
    const t = setInterval(() => void api.presence(tid, true).catch(() => undefined), 30_000);
    return () => {
      clearInterval(t);
      void api.presence(tid, false).catch(() => undefined);
    };
  }, [draft.threadId]);
  const editor = useMailEditor(
    start.eigen,
    (h) => {
      setHtml(h + zitatRef.current);
      setDirty(true);
    },
    () => setBausteine(true),
  );
  // Anhänge am Entwurf (hochgeladen oder aus dem CRM) und CRM-Vorschläge (Flyer je Termin)
  const [anhaenge, setAnhaenge] = useState<Array<{ id: number; filename: string; size: number }>>(
    [],
  );
  const [crmDocs, setCrmDocs] = useState<
    Array<{ art: 'flyer'; eventId: string; titel: string; hinweis: string }>
  >([]);
  const [anhangBusy, setAnhangBusy] = useState(false);
  const fileInp = useRef<HTMLInputElement>(null);
  useEffect(() => {
    void api
      .draftAnhaenge(draft.id)
      .then((r) => setAnhaenge(r.anhaenge))
      .catch(() => undefined);
    if (draft.threadId && bobEnabled)
      void api
        .crmAnhaenge(draft.threadId)
        .then((r) => setCrmDocs(r.vorschlaege))
        .catch(() => undefined);
  }, [draft.id, draft.threadId, bobEnabled]);
  const anhangHoch = async (file: File) => {
    setAnhangBusy(true);
    try {
      const r = await api.draftAnhangUpload(d.id, file);
      setAnhaenge((l) => [...l, r.anhang]);
      setD((x) => ({ ...x, attachmentRefs: r.attachmentRefs }));
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'Upload fehlgeschlagen');
    } finally {
      setAnhangBusy(false);
    }
  };
  const anhangCrm = async (v: { art: 'flyer'; eventId: string; titel: string }) => {
    setAnhangBusy(true);
    try {
      const r = await api.draftAnhangCrm(d.id, { art: v.art, eventId: v.eventId });
      setAnhaenge((l) => [...l, r.anhang]);
      setD((x) => ({ ...x, attachmentRefs: r.attachmentRefs }));
      toast(`${v.titel} angehängt`);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : 'CRM nicht erreichbar');
    } finally {
      setAnhangBusy(false);
    }
  };
  const anhangWeg = async (id: number) => {
    const r = await api.draftAnhangDelete(d.id, id);
    setAnhaenge((l) => l.filter((a) => a.id !== id));
    setD((x) => ({ ...x, attachmentRefs: r.attachmentRefs }));
  };
  const bausteinEinfuegen = (b: { html: string }) => {
    setBausteine(false);
    if (!editor) return;
    editor.chain().focus().insertContent(b.html).run();
  };
  const pruefen = countPruefen(html);
  const zitatSetzen = (z: string) => {
    zitatRef.current = z;
    setZitat(z);
    setHtml((editor?.getHTML() ?? '') + z);
    setDirty(true);
  };
  // Zitat doch im Editor bearbeiten (dann wie früher als Fließtext, Rahmen und Tabellen gehen verloren)
  const zitatBearbeiten = () => {
    if (!editor || !zitat) return;
    const z = zitat;
    zitatSetzen('');
    editor.chain().insertContentAt(editor.state.doc.content.size, z).run();
  };

  // Automatisch speichern (1,5 s nach der letzten Änderung), IMAP-Drafts bekommt der Worker gebündelt
  const saveRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const save = useCallback(async () => {
    setSaving('speichert');
    try {
      await api.saveDraft(d.id, {
        to: parseAddresses(to),
        cc: parseAddresses(cc),
        subject,
        html,
        mailboxId,
      });
      setDirty(false);
      setSaving('gespeichert');
    } catch {
      setSaving('');
    }
  }, [d.id, to, cc, subject, html, mailboxId]);
  useEffect(() => {
    if (!dirty) return;
    if (saveRef.current) clearTimeout(saveRef.current);
    saveRef.current = setTimeout(() => void save(), 1500);
    return () => {
      if (saveRef.current) clearTimeout(saveRef.current);
    };
  }, [dirty, save, to, cc, subject, html, mailboxId]);

  // Beim Verlassen (anderer Thread, Fenster zu, Neuladen) nicht auf den Timer warten: sofort sichern,
  // damit kein versehentlicher Klick den Entwurf kostet (D-068).
  const latest = useRef({ to, cc, subject, html, mailboxId, dirty });
  latest.current = { to, cc, subject, html, mailboxId, dirty };
  useEffect(() => {
    const flush = (keepalive: boolean) => {
      const l = latest.current;
      if (!l.dirty) return;
      if (saveRef.current) clearTimeout(saveRef.current);
      void api
        .saveDraft(
          d.id,
          {
            to: parseAddresses(l.to),
            cc: parseAddresses(l.cc),
            subject: l.subject,
            html: l.html,
            mailboxId: l.mailboxId,
          },
          true,
          keepalive ? { keepalive: true } : undefined,
        )
        .catch(() => undefined);
    };
    const beimVerlassen = () => flush(true);
    window.addEventListener('pagehide', beimVerlassen);
    return () => {
      window.removeEventListener('pagehide', beimVerlassen);
      flush(false);
    };
  }, [d.id]);

  // Verwerfen löscht endgültig (DB und IMAP-Entwürfe) – deshalb immer eine Rückfrage
  const verwerfen = () => {
    if (!window.confirm('Entwurf wirklich verwerfen? Der Text geht verloren.')) return;
    onDiscard();
  };

  const runBob = async (anweisung: string) => {
    if (!d.threadId || bob.running) return;
    setBob({ running: true, result: null });
    try {
      const r = await api.bob({ threadId: d.threadId, draftId: d.id, anweisung, html });
      setBob({ running: false, result: r });
      if (r.html && editor) {
        const t = splitZitat(r.html);
        editor.commands.setContent(t.eigen);
        zitatRef.current = t.zitat;
        setZitat(t.zitat);
        setHtml(t.eigen + t.zitat);
        setDirty(true);
        setD({ ...d, quelle: 'bob' });
      }
      if (r.karten.length) setKarten((k) => [...k, ...r.karten]);
      if (r.error) toast(r.error);
    } catch (e) {
      setBob({ running: false, result: null });
      toast(e instanceof ApiError ? e.message : 'Bob ist gerade nicht erreichbar.');
    }
  };

  const karteAusfuehren = async (k: BobKarte) => {
    try {
      const r = await api.bobKarte({ token: k.token, titel: k.titel ?? '' });
      setKarten((l) =>
        l.map((x) =>
          x.id === k.id
            ? { ...x, status: r.ok ? 'ok' : 'fehler', meldung: r.meldung ?? r.fehler ?? '' }
            : x,
        ),
      );
    } catch (e) {
      setKarten((l) =>
        l.map((x) =>
          x.id === k.id
            ? { ...x, status: 'fehler', meldung: e instanceof Error ? e.message : 'Fehler' }
            : x,
        ),
      );
    }
  };

  const send = async (thenDone: boolean, sendAt?: string) => {
    if (sending) return;
    const empf = parseAddresses(to);
    if (!empf.length) return toast('Bitte einen Empfänger eintragen.');
    if (pruefen) return toast(`Im Text stehen noch ${pruefen} [PRÜFEN]-Stellen.`);
    // Warnungen (2.A): vergessener Anhang, falsches Postfach, Sie/Du gemischt — einmal bestätigen lassen
    const warnungen = sendeWarnungen({ html, mailboxId, draft: d, mailboxes, to: empf });
    if (warnungen.length && !bestaetigt) {
      setBestaetigt(true);
      return toast(
        `Bitte prüfen: ${warnungen.join(' · ')} — noch einmal auf Senden klicken, wenn es so passt.`,
        { ms: 8000 },
      );
    }
    setSending(true);
    try {
      if (saveRef.current) clearTimeout(saveRef.current);
      await api.saveDraft(
        d.id,
        { to: empf, cc: parseAddresses(cc), subject, html, mailboxId },
        false,
      );
      const r = await api.send(d.id, thenDone, sendAt);
      onSent({
        thenDone,
        draftId: d.id,
        ...(sendAt ? { sendAt: r.sendAt } : {}),
        undo: async () => {
          await api.undo(d.id);
        },
      });
    } catch (e) {
      setSending(false);
      toast(e instanceof ApiError ? e.message : 'Senden fehlgeschlagen.');
    }
  };

  const mb = mailboxes.find((m) => m.id === mailboxId);
  return (
    <section className="reply" aria-label="Antwort">
      <div className="ed-f">
        {pruefen > 0 && (
          <span className="warnline">
            {pruefen} Stelle{pruefen > 1 ? 'n' : ''} mit [PRÜFEN] – vor dem Senden klären
          </span>
        )}
        {mb && me && mb.visibility === 'personal' && mb.owner !== me.key && (
          <span className="warnline">Fremdes persönliches Postfach</span>
        )}
        <span className="sp" />
        <button className="btn ghost" onClick={verwerfen}>
          Verwerfen
        </button>
        <button className="btn" onClick={() => void save()} disabled={!dirty}>
          Entwurf sichern
        </button>
        {d.threadId ? (
          <>
            <button
              className="btn"
              onClick={() => void send(false)}
              disabled={sending || pruefen > 0}
              title="Thread bleibt im Posteingang"
            >
              Senden, offen lassen
            </button>
            <button
              className="btn pri"
              onClick={() => void send(true)}
              disabled={sending || pruefen > 0}
              id="sendDone"
            >
              Senden &amp; erledigen
            </button>
          </>
        ) : (
          <button
            className="btn pri"
            onClick={() => void send(false)}
            disabled={sending || pruefen > 0}
          >
            Senden
          </button>
        )}
      </div>
      {d.threadId && (
        <>
          <div className="bobbar">
            <div className="bob-av" aria-hidden="true">
              ✦
            </div>
            <input
              id="bobIn"
              placeholder={
                bobEnabled
                  ? 'Bob sagen, was in die Antwort soll …'
                  : 'Bob sagen, was in die Antwort soll (Hört Hin!: Ausbildung, minimusiker+, Medienzentren) …'
              }
              value={bobText}
              onChange={(e) => setBobText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && bobText.trim()) void runBob(bobText.trim());
              }}
              disabled={bob.running}
            />
            <button
              className="btn bob"
              onClick={() => void runBob(bobText.trim() || 'Antworte auf alle Fragen der Mail.')}
              disabled={bob.running}
            >
              {bob.running ? 'Schreibt …' : 'Entwurf'}
            </button>
          </div>
          {!bob.running && !bob.result && chips.length > 0 && (
            <div className="chips">
              {chips.map((c) => (
                <button key={c} className="chip" onClick={() => void runBob(c)}>
                  {c}
                </button>
              ))}
              {leadChip && (
                <button
                  className="chip"
                  onClick={() =>
                    void runBob(
                      'Leg aus dieser Anfrage einen Lead im CRM an (lead_anlegen_vorbereiten, Daten aus Signatur und Mail, nichts erfinden) und schreib danach eine kurze Eingangsbestätigung mit den nächsten Schritten.',
                    )
                  }
                >
                  ✦ Lead anlegen (Karte)
                </button>
              )}
              {leadChip && (
                <button
                  className="chip"
                  title="Einrichtung und Ansprechperson im CRM anlegen, ohne Lead (z. B. bestehende Kundschaft)"
                  onClick={() =>
                    void runBob(
                      'Leg aus dieser Mail die Einrichtung samt Ansprechperson im CRM an – ohne Lead (aendern_vorbereiten mit ziel einrichtung und anlegen=true, Daten aus Signatur und Mail, nichts erfinden) – und schreib danach eine kurze Antwort.',
                    )
                  }
                >
                  ✦ Einrichtung anlegen (Karte)
                </button>
              )}
            </div>
          )}
          {(bob.running || bob.result) && (
            <div className="steps">
              {bob.running && (
                <div>
                  <span className="spin" /> Bob liest die Mail, holt den CRM-Kontext und schreibt …
                </div>
              )}
              {bob.result?.schritte.map((s, i) => (
                <div key={i}>
                  {s.ok ? '✓' : '✗'} <code>{s.tool}</code>
                  <span className="muted">
                    {typeof s.input === 'object' && s.input
                      ? JSON.stringify(s.input).slice(0, 80)
                      : ''}
                  </span>
                </div>
              ))}
              {bob.result && !bob.result.html && bob.result.antwort && (
                <div style={{ color: 'var(--fg)' }}>{bob.result.antwort}</div>
              )}
              {bob.result && bob.result.kostenEur > 0 && (
                <div className="muted">{bob.result.kostenEur.toFixed(3)} €</div>
              )}
            </div>
          )}
        </>
      )}
      <div className="ed-h">
        <span className="lab">Von</span>
        <select
          aria-label="Absender"
          value={mailboxId}
          onChange={(e) => (setMailboxId(Number(e.target.value)), setDirty(true))}
        >
          {mailboxes.map((x) => (
            <option key={x.id} value={x.id}>
              {x.address}
            </option>
          ))}
        </select>
        {d.quelle === 'bob' && <span className="pill bob">✦ von Bob, bitte gegenlesen</span>}
        <span className="muted" style={{ marginLeft: 'auto' }}>
          {saving === 'speichert'
            ? 'speichert …'
            : saving === 'gespeichert' && !dirty
              ? 'Entwurf gespeichert'
              : ''}
        </span>
      </div>
      <div className="ed-h">
        <span className="lab">An</span>
        <AdressFeld
          ariaLabel="Empfänger"
          value={to}
          onChange={(v) => (setTo(v), setDirty(true))}
          placeholder="Name, Schule oder Adresse – Vorschläge aus CRM und Verlauf"
        />
        {!showCc && (
          <button className="btn ghost sm" onClick={() => setShowCc(true)}>
            Cc
          </button>
        )}
      </div>
      {showCc && (
        <div className="ed-h">
          <span className="lab">Cc</span>
          <AdressFeld ariaLabel="Cc" value={cc} onChange={(v) => (setCc(v), setDirty(true))} />
        </div>
      )}
      {(standalone || !d.threadId) && (
        <div className="ed-h">
          <span className="lab">Betreff</span>
          <input
            aria-label="Betreff"
            value={subject}
            onChange={(e) => (setSubject(e.target.value), setDirty(true))}
          />
        </div>
      )}
      <div className="ed-tools">
        <button
          className="btn ghost sm"
          onClick={() => setBausteine((v) => !v)}
          title="Textbaustein einfügen (/ am Zeilenanfang)"
        >
          ¶ Bausteine
        </button>
        <button
          className="btn ghost sm"
          onClick={() => fileInp.current?.click()}
          disabled={anhangBusy}
          title="Datei anhängen"
        >
          📎 Datei
        </button>
        {editor && <Formatleiste editor={editor} />}
        <input
          ref={fileInp}
          type="file"
          hidden
          multiple
          onChange={(e) => {
            for (const f of Array.from(e.target.files ?? [])) void anhangHoch(f);
            e.target.value = '';
          }}
        />
        {crmDocs
          .filter((v) => !anhaenge.some((a) => /flyer/i.test(a.filename) && v.art === 'flyer'))
          .map((v) => (
            <button
              key={v.eventId}
              className="chip crm"
              onClick={() => void anhangCrm(v)}
              disabled={anhangBusy}
              title={v.hinweis}
            >
              📎 {v.titel} aus dem CRM
            </button>
          ))}
        {anhaenge.map((a) => (
          <span key={a.id} className="att-chip">
            <a href={`/api/attachments/${a.id}?download=1`}>{a.filename}</a>
            <span className="muted"> {bytes(a.size)}</span>
            <button aria-label={`${a.filename} entfernen`} onClick={() => void anhangWeg(a.id)}>
              ✕
            </button>
          </span>
        ))}
      </div>
      {bausteine && (
        <BausteinePopover
          threadId={d.threadId}
          onPick={bausteinEinfuegen}
          onClose={() => setBausteine(false)}
        />
      )}
      <MailEditor editor={editor} />
      {karten.map((k) => (
        <div className="card" key={k.id}>
          <div className="bob-av" style={{ width: 22, height: 22, fontSize: 11 }}>
            ✦
          </div>
          <div className="tx">
            {k.titel ?? k.typ}
            <small>
              {k.status === 'ok'
                ? k.meldung || 'übernommen'
                : k.status === 'fehler'
                  ? `Fehler: ${k.meldung}`
                  : 'Bestätigungskarte aus dem CRM · nichts passiert ohne Klick'}
            </small>
          </div>
          {!k.status && (
            <>
              <button
                className="btn"
                onClick={() =>
                  setKarten((l) =>
                    l.map((x) => (x.id === k.id ? { ...x, status: 'verworfen' } : x)),
                  )
                }
              >
                Verwerfen
              </button>
              <button className="btn pri" onClick={() => void karteAusfuehren(k)}>
                {k.knopf ?? 'Ausführen'}
              </button>
            </>
          )}
          {k.status === 'ok' && <span className="pill ok">übernommen</span>}
          {k.status === 'verworfen' && <span className="pill mut">verworfen</span>}
        </div>
      ))}
      {zitat && (
        <details className="ed-quote">
          <summary>
            <span className="mm-auf">··· Zitierten Verlauf anzeigen</span>
            <span className="mm-zu">Zitierten Verlauf ausblenden</span>
            <button
              type="button"
              className="lnk"
              title="Zitat als Text in den Editor übernehmen"
              onClick={(e) => (e.preventDefault(), zitatBearbeiten())}
            >
              bearbeiten
            </button>
            <button
              type="button"
              className="lnk"
              title="Ohne zitierten Verlauf antworten"
              onClick={(e) => (e.preventDefault(), zitatSetzen(''))}
            >
              entfernen
            </button>
          </summary>
          <div className="ed-quote-body" dangerouslySetInnerHTML={{ __html: zitatVorschau(zitat) }} />
        </details>
      )}
    </section>
  );
}

export { PRUEFEN_RE };

// Vorschläge für „Später senden“: nächster Morgen 7:30, Montag 7:30, in 2 Stunden
export function spaeterOptionen(now = new Date()): Array<{ label: string; at: string }> {
  const um = (d: Date, h: number, m: number) => {
    const x = new Date(d);
    x.setHours(h, m, 0, 0);
    return x;
  };
  const morgen = um(new Date(now.getTime() + 864e5), 7, 30);
  const montag = (() => {
    const d = new Date(now);
    const bis = (8 - d.getDay()) % 7 || 7;
    return um(new Date(now.getTime() + bis * 864e5), 7, 30);
  })();
  const inZwei = new Date(now.getTime() + 2 * 3600e3);
  const fmt = (d: Date) =>
    d.toLocaleString('de-DE', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  return [
    { label: `Morgen 7:30 (${fmt(morgen)})`, at: morgen.toISOString() },
    { label: `Montag 7:30 (${fmt(montag)})`, at: montag.toISOString() },
    { label: `In 2 Stunden (${fmt(inZwei)})`, at: inZwei.toISOString() },
  ];
}
